import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get('/auth/me'),
  getProfile: () => apiClient.get('/auth/me'),
  getPermissions: (companyId) => apiClient.get(`/auth/my-permissions/${companyId}`),
  switchCompany: (companyId) => apiClient.post('/auth/switch-company', { companyId }),
  getLoginConfig: () => apiClient.get('/auth/login-config'),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => apiClient.get('/invoices', { params }),
  getById: (id) => apiClient.get(`/invoices/${id}`),
  create: (data) => apiClient.post('/invoices', data),
  update: (id, data) => apiClient.put(`/invoices/${id}`, data),
  delete: (id, deleteType = 'hard') => apiClient.delete(`/invoices/${id}?deleteType=${deleteType}`),
  getDeleted: () => apiClient.get('/invoices/deleted'),
  restore: (id) => apiClient.post(`/invoices/${id}/restore`),
  recalculateCurrency: (id, data) => apiClient.post(`/invoices/${id}/recalculate-currency`, data),
  getHistory: (id) => apiClient.get(`/invoices/${id}/history`),
};

// Currencies API
export const currenciesAPI = {
  getAll: async () => {
    const response = await apiClient.get('/currencies/all');
    return response;
  },
  create: (data) => apiClient.post('/currencies', data),
  update: (code, data) => apiClient.put(`/currencies/${code}`, data),
};

// GL Accounts API
export const glAccountsAPI = {
  getAll: () => apiClient.get('/gl-accounts'),
  getActive: () => apiClient.get('/gl-accounts/active'),
  create: (data) => apiClient.post('/gl-accounts', data),
  update: (id, data) => apiClient.put(`/gl-accounts/${id}`, data),
  delete: (id) => apiClient.delete(`/gl-accounts/${id}`),
  importExcel: (formData) => apiClient.post('/gl-accounts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importFromIntegration: () => apiClient.post('/gl-accounts/import-from-integration'),
};

// Projects API
export const projectsAPI = {
  getAll: () => apiClient.get('/projects'),
  getActive: () => apiClient.get('/projects/active'),
  getById: (id) => apiClient.get(`/projects/${id}`),
  create: (data) => apiClient.post('/projects', data),
  update: (id, data) => apiClient.put(`/projects/${id}`, data),
  delete: (id) => apiClient.delete(`/projects/${id}`),
  importExcel: (formData) => apiClient.post('/projects/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Project Lines
  createLine: (projectId, data) => apiClient.post(`/projects/${projectId}/lines`, data),
  updateLine: (projectId, lineId, data) => apiClient.put(`/projects/${projectId}/lines/${lineId}`, data),
  deleteLine: (projectId, lineId) => apiClient.delete(`/projects/${projectId}/lines/${lineId}`),
  importLines: (projectId, formData) => apiClient.post(`/projects/${projectId}/lines/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Users API
export const usersAPI = {
  getAll: (params) => apiClient.get('/users', { params }),
  getById: (id) => apiClient.get(`/users/${id}`),
  create: (data) => apiClient.post('/users', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  delete: (id) => apiClient.delete(`/users/${id}`),
  getDepartments: (id) => apiClient.get(`/users/${id}/departments`),
  assignDepartments: (id, data) => apiClient.post(`/users/${id}/departments`, data),
  removeDepartment: (id, deptId) => apiClient.delete(`/users/${id}/departments/${deptId}`),
};

// Companies API
export const companiesAPI = {
  getAll: (params) => apiClient.get('/companies', { params }),
  getById: (id) => apiClient.get(`/companies/${id}`),
  create: (data) => apiClient.post('/companies', data),
  update: (id, data) => apiClient.put(`/companies/${id}`, data),
};

// Budgets API
export const budgetsAPI = {
  getAll: (params) => apiClient.get('/budgets', { params }),
  getById: (id) => apiClient.get(`/budgets/${id}`),
  getForManagement: (id) => apiClient.get(`/budgets/${id}/manage`),
  create: (data) => apiClient.post('/budgets', data),
  update: (id, data) => apiClient.put(`/budgets/${id}`, data),
  delete: (id) => apiClient.delete(`/budgets/${id}`),
  clone: (id, data) => apiClient.post(`/budgets/${id}/clone`, data),
  submitForApproval: (id, notes) => apiClient.post(`/budgets/${id}/submit-for-approval`, { notes }),
  approve: (id, notes) => apiClient.post(`/budgets/${id}/approve`, { notes }),
  reject: (id, notes) => apiClient.post(`/budgets/${id}/reject`, { notes }),
  getDashboard: () => apiClient.get('/budgets/dashboard'),
  getMyDepartmentBudgets: () => apiClient.get('/budgets/my-departments'),
  // Budget Lines
  createLine: (budgetId, data) => apiClient.post(`/budgets/${budgetId}/lines`, data),
  updateLine: (budgetId, lineId, data) => apiClient.put(`/budgets/${budgetId}/lines/${lineId}`, data),
  deleteLine: (budgetId, lineId) => apiClient.delete(`/budgets/${budgetId}/lines/${lineId}`),
  importLines: (budgetId, formData) => apiClient.post(`/budgets/${budgetId}/lines/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMonthlyView: (budgetId, viewMode = 'equal_spread') => apiClient.get(`/budget-monthly/${budgetId}`, { params: { viewMode } }),
  getLinesByDepartment: (departmentId) => apiClient.get('/budgets/lines', { params: { departmentId } }),
  getLineTransactions: (budgetId, lineId) => apiClient.get(`/budgets/${budgetId}/lines/${lineId}/transactions`),
  getCommittedPOs: (budgetId, lineId) => apiClient.get(`/budgets/${budgetId}/lines/${lineId}/committed-pos`),
  updateForecast: (budgetId, lineId, data) => apiClient.patch(`/budgets/${budgetId}/lines/${lineId}/forecast`, data),
  getForecastHistory: (budgetId, lineId) => apiClient.get(`/budgets/${budgetId}/lines/${lineId}/forecast/history`),
  getAnalytics: (budgetId) => apiClient.get(`/budgets/${budgetId}/analytics`),
  getLineDrillDown: (budgetId, lineId) => apiClient.get(`/budgets/${budgetId}/lines/${lineId}/drill-down`),
  getCompanyAnalytics: () => apiClient.get('/budgets/company-analytics'),
  // Overflow
  getOverflowSummary: () => apiClient.get('/budgets/overflow-summary'),
  getBudgetOverflow: (budgetId, params) => apiClient.get(`/budgets/${budgetId}/overflow`, { params }),
  resolveOverflow: (lineId, data) => apiClient.put(`/budgets/overflow/${lineId}/resolve`, data),
  reassignOverflow: (lineId, targetBudgetLineId) => apiClient.put(`/budgets/overflow/${lineId}/reassign`, { targetBudgetLineId }),
};

// Financial Years API
export const financialYearsAPI = {
  getAll: () => apiClient.get('/financial-years'),
  getCurrent: () => apiClient.get('/financial-years/current'),
  create: (data) => apiClient.post('/financial-years', data),
  update: (id, data) => apiClient.put(`/financial-years/${id}`, data),
  setCurrent: (id) => apiClient.put(`/financial-years/${id}/set-current`),
  delete: (id) => apiClient.delete(`/financial-years/${id}`),
};

// Vendors API
export const vendorsAPI = {
  getAll: async (params) => {
    const response = await apiClient.get('/vendors', { params });
    return response;
  },
  getById: (id) => apiClient.get(`/vendors/${id}`),
  create: (data) => apiClient.post('/vendors', data),
  update: (id, data) => apiClient.put(`/vendors/${id}`, data),
  approve: (id) => apiClient.put(`/vendors/${id}/approve`),
};

// Departments API
export const departmentsAPI = {
  getAll: () => apiClient.get('/departments'),
  getById: (id) => apiClient.get(`/departments/${id}`),
  create: (data) => apiClient.post('/departments', data),
  update: (id, data) => apiClient.put(`/departments/${id}`, data),
  addUser: (id, data) => apiClient.post(`/departments/${id}/users`, data),
  removeUser: (id, userId) => apiClient.delete(`/departments/${id}/users/${userId}`),
};

// Reports API
export const reportsAPI = {
  getInvoices: (params) => apiClient.get('/reports/invoices', { params }),
  getBudgets: (params) => apiClient.get('/reports/budgets', { params }),
  getSpending: (params) => apiClient.get('/reports/spending', { params }),
  getVendorAnalysis: (params) => apiClient.get('/reports/vendor-analysis', { params }),
  getGLAnalysis: (params) => apiClient.get('/reports/gl-analysis', { params }),
  getYoYBudget: (params) => apiClient.get('/reports/yoy-budget', { params }),
  getYoYSpending: (params) => apiClient.get('/reports/yoy-spending', { params }),
  getAging: (params) => apiClient.get('/reports/aging', { params }),
  getApprovalPipeline: (params) => apiClient.get('/reports/approval-pipeline', { params }),
  exportCSV: (params) => apiClient.get('/reports/export', { 
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
};

// Approvals API
export const approvalsAPI = {
  getPending: () => apiClient.get('/approvals/pending'),
  getPendingCount: () => apiClient.get('/approvals/pending-count'),
  getHistory: (params) => apiClient.get('/approvals/history', { params }),
  approve: (id, data) => apiClient.post(`/approvals/${id}/approve`, data),
  reject: (id, data) => apiClient.post(`/approvals/${id}/reject`, data),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => apiClient.get('/dashboard/summary'),
  getRecentInvoices: (limit) => apiClient.get('/dashboard/recent-invoices', { params: { limit } }),
  getInvoicesByStatus: () => apiClient.get('/dashboard/charts/invoices-by-status'),
  getInvoicesByMonth: (months) => apiClient.get('/dashboard/charts/invoices-by-month', { params: { months } }),
  getTopVendors: (limit) => apiClient.get('/dashboard/charts/top-vendors', { params: { limit } }),
  getBudgetUtilization: () => apiClient.get('/dashboard/charts/budget-utilization'),
};

// Documents API
export const documentsAPI = {
  getByInvoice: (invoiceId) => apiClient.get(`/documents/invoice/${invoiceId}`),
  upload: (invoiceId, data) => apiClient.post(`/documents/invoice/${invoiceId}`, data),
  delete: (id) => apiClient.delete(`/documents/${id}`),
  getDownloadUrl: (id) => apiClient.get(`/documents/${id}/download-url`),
};

// Settings API
export const settingsAPI = {
  getPreferences: () => apiClient.get('/settings/preferences'),
  updatePreference: (key, value) => apiClient.put(`/settings/preferences/${key}`, { value }),
  getSavedViews: () => apiClient.get('/settings/saved-views'),
  createSavedView: (data) => apiClient.post('/settings/saved-views', data),
  deleteSavedView: (id) => apiClient.delete(`/settings/saved-views/${id}`),
  getCompanyAuth: () => apiClient.get('/settings/company-auth'),
  updateCompanyAuth: (data) => apiClient.put('/settings/company-auth', data),
  getRoles: () => apiClient.get('/settings/roles'),
  getPermissions: () => apiClient.get('/settings/permissions'),
  // SSO configuration
  getSSO: () => apiClient.get('/settings/sso'),
  updateSSO: (data) => apiClient.post('/settings/sso', data),
  testSSO: (data) => apiClient.post('/settings/sso/test', data),
  getCompanySSO: (companyId) => apiClient.get(`/settings/company-sso/${companyId}`),
  updateCompanySSO: (companyId, data) => apiClient.put(`/settings/company-sso/${companyId}`, data),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => apiClient.get('/expenses', { params }),
  getById: (id) => apiClient.get(`/expenses/${id}`),
  create: (data) => apiClient.post('/expenses', data),
  update: (id, data) => apiClient.put(`/expenses/${id}`, data),
  delete: (id) => apiClient.delete(`/expenses/${id}`),
  submit: (id) => apiClient.post(`/expenses/${id}/submit`),
  approve: (id, data) => apiClient.post(`/expenses/${id}/approve`, data),
  reject: (id, data) => apiClient.post(`/expenses/${id}/reject`, data),
};

// Subscriptions API
export const subscriptionsAPI = {
  getAll: (params) => apiClient.get('/subscriptions', { params }),
  getById: (id) => apiClient.get(`/subscriptions/${id}`),
  create: (data) => apiClient.post('/subscriptions', data),
  update: (id, data) => apiClient.put(`/subscriptions/${id}`, data),
  delete: (id) => apiClient.delete(`/subscriptions/${id}`),
};

// Exchange Rates API (under /currencies)
export const exchangeRatesAPI = {
  getAll: (params) => apiClient.get('/currencies/exchange-rates', { params }),
  getLatest: (params) => apiClient.get('/currencies/exchange-rates/latest', { params }),
  create: (data) => apiClient.post('/currencies/exchange-rates', data),
  update: (id, data) => apiClient.put(`/currencies/exchange-rates/${id}`, data),
  delete: (id) => apiClient.delete(`/currencies/exchange-rates/${id}`),
  getCompanyDefault: () => apiClient.get('/currencies/company-default'),
  updateCompanyDefault: (data) => apiClient.put('/currencies/company-default', data),
  convert: (data) => apiClient.post('/currencies/convert', data),
  fetchLatest: () => apiClient.post('/currencies/fetch-latest-rates'),
};

// Audit Logs API
export const auditLogsAPI = {
  getAll: (params) => apiClient.get('/audit-logs', { params }),
  getByInvoice: (invoiceId) => apiClient.get(`/audit-logs/invoice/${invoiceId}`),
  export: (params) => apiClient.get('/audit-logs/export', { params, responseType: 'blob' }),
};

// Platform API (Super Admin)
export const platformAPI = {
  getCompanies: () => apiClient.get('/platform/companies'),
  getUsers: () => apiClient.get('/platform/users'),
  getActivity: () => apiClient.get('/platform/activity'),
  getOwners: () => apiClient.get('/platform/owners'),
};

// Department Approvals API
export const departmentApprovalsAPI = {
  getApprovers: (deptId) => apiClient.get(`/department-approvals/${deptId}/approvers`),
  addApprover: (deptId, data) => apiClient.post(`/department-approvals/${deptId}/approvers`, data),
  removeApprover: (deptId, userId) => apiClient.delete(`/department-approvals/${deptId}/approvers/${userId}`),
  getHierarchy: (deptId) => apiClient.get(`/department-approvals/${deptId}/hierarchy`),
  getAvailableUsers: (deptId) => apiClient.get(`/department-approvals/${deptId}/available-users`),
};

// Vendor Approvals API
export const vendorApprovalsAPI = {
  get: (vendorId) => apiClient.get(`/vendor-approvals/${vendorId}`),
  save: (vendorId, data) => apiClient.post(`/vendor-approvals/${vendorId}`, data),
  approve: (vendorId) => apiClient.post(`/vendor-approvals/${vendorId}/approve`),
  uploadContract: (vendorId, formData) => apiClient.post(`/vendor-approvals/${vendorId}/upload-contract`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Manager Settings API
export const managerSettingsAPI = {
  getApprovers: (deptId) => apiClient.get(`/manager-settings/${deptId}/approvers`),
  addApprover: (deptId, data) => apiClient.post(`/manager-settings/${deptId}/approvers`, data),
  removeApprover: (deptId, userId) => apiClient.delete(`/manager-settings/${deptId}/approvers/${userId}`),
  updateThreshold: (deptId, data) => apiClient.put(`/manager-settings/${deptId}/threshold`, data),
};

// Mileage API
export const mileageAPI = {
  getRates: () => apiClient.get('/mileage/rates'),
  updateRate: (id, data) => apiClient.put(`/mileage/rates/${id}`, data),
  createRate: (data) => apiClient.post('/mileage/rates', data),
  getCompanyRates: () => apiClient.get('/mileage/rates/company'),
  saveCompanyRates: (data) => apiClient.post('/mileage/rates/company/bulk', data),
  deleteCompanyRates: () => apiClient.delete('/mileage/rates/company'),
};

// Invoice Reconciliation API
export const invoiceReconciliationAPI = {
  getPending: () => apiClient.get('/invoice-reconciliation/pending'),
  confirmRate: (invoiceId, data) => apiClient.post(`/invoice-reconciliation/${invoiceId}/confirm-rate`, data),
  getHistory: (invoiceId) => apiClient.get(`/invoice-reconciliation/${invoiceId}/history`),
};

// Finance Dashboard API
export const financeDashboardAPI = {
  getFilters: () => apiClient.get('/finance-dashboard/filters'),
  getOverview: (params) => apiClient.get('/finance-dashboard/overview', { params }),
  getTrends: (params) => apiClient.get('/finance-dashboard/trends', { params }),
  getInvoices: (params) => apiClient.get('/finance-dashboard/invoices', { params }),
  getExpenses: (params) => apiClient.get('/finance-dashboard/expenses', { params }),
  getBudgets: (params) => apiClient.get('/finance-dashboard/budgets', { params }),
  getVat: (params) => apiClient.get('/finance-dashboard/vat', { params }),
  getByVendor: (params) => apiClient.get('/finance-dashboard/by-vendor', { params }),
  getByNominalCode: (params) => apiClient.get('/finance-dashboard/by-nominal-code', { params }),
  getApproverPerformance: (params) => apiClient.get('/finance-dashboard/approver-performance', { params }),
};

// Approval SLAs API
export const approvalSLAsAPI = {
  getAll: () => apiClient.get('/approval-slas'),
  getById: (id) => apiClient.get(`/approval-slas/${id}`),
  create: (data) => apiClient.post('/approval-slas', data),
  update: (id, data) => apiClient.put(`/approval-slas/${id}`, data),
  delete: (id) => apiClient.delete(`/approval-slas/${id}`),
  getCompliance: (params) => apiClient.get('/approval-slas/reports/compliance', { params }),
};

// Help Center API — backend write endpoints expect camelCase, frontend forms use PascalCase
const toBody = (f) => ({
  categoryId: f.CategoryID,
  slug: f.Slug,
  title: f.Title,
  summary: f.Summary,
  bodyMarkdown: f.BodyMarkdown,
  sortOrder: f.SortOrder,
  isPublished: !!f.IsPublished,
  scope: f.Scope === 'global' ? 'global' : 'company',
});
export const helpAPI = {
  // Public (auth-only) reads
  getCategories: () => apiClient.get('/help/categories'),
  getArticles: (params) => apiClient.get('/help/articles', { params }),
  getArticle: (slug) => apiClient.get(`/help/articles/${slug}`),
  // Admin
  adminListArticles: () => apiClient.get('/help/admin/articles'),
  adminGetArticle: (id) => apiClient.get(`/help/admin/articles/${id}`),
  adminCreateArticle: (form) => apiClient.post('/help/admin/articles', toBody(form)),
  adminUpdateArticle: (id, form) => apiClient.put(`/help/admin/articles/${id}`, toBody(form)),
  adminDeleteArticle: (id) => apiClient.delete(`/help/admin/articles/${id}`),
  adminUploadImage: (file, scope = 'company') => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('scope', scope);
    return apiClient.post('/help/admin/upload-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Tickets
  createTicket: (f) => apiClient.post('/help/tickets', {
    subject: f.Subject, body: f.Body, category: f.Category, priority: f.Priority, pageUrl: f.PageUrl,
  }),
  listMyTickets:  () => apiClient.get('/help/tickets/my'),
  getOpenCount:   () => apiClient.get('/help/tickets/open-count'),
  listTickets:    (params) => apiClient.get('/help/tickets', { params }),
  getTicket:     (id) => apiClient.get(`/help/tickets/${id}`),
  updateTicket:  (id, f) => apiClient.put(`/help/tickets/${id}`, {
    status: f.Status ?? f.status,
    resolutionNotes: f.ResolutionNotes ?? f.resolutionNotes,
    assignedToUserId: f.assignedToUserId ?? null,
  }),
  getMessages: (id) => apiClient.get(`/help/tickets/${id}/messages`),
  addMessage:  (id, body, isInternal = false) =>
    apiClient.post(`/help/tickets/${id}/messages`, { body, isInternal }),
};

// Scheduler & Notifications Hub API
export const schedulerAPI = {
  // ── Scheduled Tasks ──────────────────────────────────────────────
  getTasks:    (companyId) => apiClient.get('/scheduler/tasks',    { params: { companyId } }),
  updateTask:  (id, data, companyId) => apiClient.put(`/scheduler/tasks/${id}`, { ...data, companyId }),
  runTask:     (id, companyId) => apiClient.post(`/scheduler/tasks/${id}/run`, { companyId }),
  getTaskLog:  (id, companyId) => apiClient.get(`/scheduler/tasks/${id}/log`, { params: { companyId } }),

  // ── Notification Rules ───────────────────────────────────────────
  getRules:    (companyId) => apiClient.get('/scheduler/rules',    { params: { companyId } }),
  createRule:  (data)       => apiClient.post('/scheduler/rules',  data),
  updateRule:  (id, data)   => apiClient.put(`/scheduler/rules/${id}`, data),
  deleteRule:  (id, companyId) => apiClient.delete(`/scheduler/rules/${id}`, { data: { companyId } }),
  runRule:     (id, companyId) => apiClient.post(`/scheduler/rules/${id}/run`, { companyId }),
  getRuleLog:  (id, companyId) => apiClient.get(`/scheduler/rules/${id}/log`, { params: { companyId } }),

  // ── Recipients ───────────────────────────────────────────────────
  getRecipients:   (ruleId, companyId) => apiClient.get(`/scheduler/rules/${ruleId}/recipients`, { params: { companyId } }),
  addRecipient:    (ruleId, data)      => apiClient.post(`/scheduler/rules/${ruleId}/recipients`, data),
  removeRecipient: (ruleId, recipientId, companyId) => apiClient.delete(`/scheduler/rules/${ruleId}/recipients/${recipientId}`, { data: { companyId } }),

  // ── User picker ──────────────────────────────────────────────────
  getUsers:   (companyId) => apiClient.get('/scheduler/users', { params: { companyId } }),

  // ── Migration ────────────────────────────────────────────────────
  migrate: () => apiClient.post('/scheduler/migrate'),
};

export const flowsAPI = {
  // ── Flows CRUD ───────────────────────────────────────────────────
  getFlows:    (companyId)          => apiClient.get('/flows', { params: { companyId } }),
  createFlow:  (companyId, data)    => apiClient.post('/flows', { ...data, companyId }),
  updateFlow:  (companyId, id, data) => apiClient.put(`/flows/${id}`, { ...data, companyId }),
  deleteFlow:  (companyId, id)      => apiClient.delete(`/flows/${id}`, { data: { companyId } }),
  runFlow:     (companyId, id)      => apiClient.post(`/flows/${id}/run`, { companyId }),
  getFlowLog:  (companyId, id)      => apiClient.get(`/flows/${id}/log`, { params: { companyId } }),

  // ── Steps ────────────────────────────────────────────────────────
  getSteps:    (companyId, flowId)              => apiClient.get(`/flows/${flowId}/steps`, { params: { companyId } }),
  addStep:     (companyId, flowId, data)        => apiClient.post(`/flows/${flowId}/steps`, { ...data, companyId }),
  reorderSteps:(companyId, flowId, orderedStepIds) => apiClient.put(`/flows/${flowId}/steps/reorder`, { orderedStepIds, companyId }),
  deleteStep:  (companyId, flowId, stepId)      => apiClient.delete(`/flows/${flowId}/steps/${stepId}`, { data: { companyId } }),
};

export const purchaseOrdersAPI = {
  // Settings
  getSettings:     ()                => apiClient.get('/purchase-orders/settings'),
  updateSettings:  (data)            => apiClient.put('/purchase-orders/settings', data),
  // List & Search
  list:            (params)          => apiClient.get('/purchase-orders', { params }),
  // CRUD
  create:          (data)            => apiClient.post('/purchase-orders', data),
  get:             (id)              => apiClient.get(`/purchase-orders/${id}`),
  update:          (id, data)        => apiClient.put(`/purchase-orders/${id}`, data),
  delete:          (id)              => apiClient.delete(`/purchase-orders/${id}`),
  // Lifecycle
  submit:          (id)              => apiClient.post(`/purchase-orders/${id}/submit`),
  approve:         (id, notes)       => apiClient.post(`/purchase-orders/${id}/approve`, { notes }),
  reject:          (id, notes)       => apiClient.post(`/purchase-orders/${id}/reject`, { notes }),
  close:           (id)              => apiClient.post(`/purchase-orders/${id}/close`),
  // Invoice matching
  matchInvoice:    (id, invoiceId, notes) => apiClient.post(`/purchase-orders/${id}/match-invoice`, { invoiceId, notes }),
  unmatchInvoice:  (id, matchId)     => apiClient.delete(`/purchase-orders/${id}/match-invoice/${matchId}`),
  getForInvoice:   (invoiceId)       => apiClient.get(`/purchase-orders/for-invoice/${invoiceId}`),
  // Send to vendor / recurring
  sendToVendor:    (id, data)        => apiClient.post(`/purchase-orders/${id}/send-to-vendor`, data),
  generateNext:    (id)              => apiClient.post(`/purchase-orders/${id}/generate-next`),
  // Auto-numbering
  getNextNumber:   ()                => apiClient.get('/purchase-orders/next-number'),
  // ERP Sync
  erpSync:         (provider)        => apiClient.post('/purchase-orders/erp-sync', { provider }),
};

// ERP Mappings API — provider-agnostic GL account / vendor / dimension mapping
export const erpMappingsAPI = {
  getAll:      (params)     => apiClient.get('/erp-mappings', { params }),
  bulkUpsert:  (data)       => apiClient.put('/erp-mappings/bulk', data),
  update:      (id, data)   => apiClient.put(`/erp-mappings/${id}`, data),
  delete:      (id)         => apiClient.delete(`/erp-mappings/${id}`),
};

export const teamsAPI = {
  // Company Admin
  getUsers:          ()        => apiClient.get('/teams-admin/users'),
  updateUser:        (id, data) => apiClient.put(`/teams-admin/users/${id}`, data),
  getUsage:          ()        => apiClient.get('/teams-admin/usage'),
  // Standup (any user)
  getSchedule:       ()        => apiClient.get('/teams-admin/standup/schedule'),
  saveSchedule:      (data)    => apiClient.put('/teams-admin/standup/schedule', data),
  deleteSchedule:    ()        => apiClient.delete('/teams-admin/standup/schedule'),
  getSessions:       ()        => apiClient.get('/teams-admin/standup/sessions'),
  // Platform Admin
  getPlatformUsage:  ()        => apiClient.get('/teams-admin/platform'),
};

// Export both default and named
export { apiClient };
export default apiClient;
